import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import HomeScreen from './home/HomeScreen';
import AiStack from './ai/AiStack';
import GoalsScreen from './goals/GoalsScreen';
import AdminScreen from './admin/AdminScreen';
import CommentsScreen from './stylist/CommentsScreen';
import StoresScreen from './stores/StoresScreen';
import { STORES_TAB_ENABLED } from '../utils/storesTab';
import { loadPendingApprovals, prefetchAdminData } from '../api/approvalsApi';
import { startPush } from '../push/pushClient';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
/* LEGACY_SHELL — kept for Phase 2+; do not delete
import Sidebar from './layout/Sidebar';
import TopBar from './layout/TopBar';
*/
import AppShell from './layout/AppShell';
import CustomerAnalysisView from './customers/CustomerAnalysisView';
import KPISection from './sections/KPISection';
import Card from './ui/Card';
import Button from './ui/Button';
import SectionHeader from './ui/SectionHeader';
import { useDashboard } from '../context/DashboardContext';
import { useAuth } from '../auth/AuthContext';
import { useUserRole, getSettingsPath } from '../hooks/useUserRole';

/** Set REACT_APP_SHOW_LEGACY_KPI=true to show legacy KPI block on ホーム tab (dev only). */
const SHOW_LEGACY_KPI = process.env.REACT_APP_SHOW_LEGACY_KPI === 'true';

const TAB_PATHS = {
  home: '/',
  ai: '/ai',
  goals: '/goals',
  stores: '/stores',
  admin: '/admin',
  comments: '/comments',
  'customer-analysis': '/customers',
  'legacy-kpi': '/legacy-kpi',
};

function pathToTab(pathname) {
  if (pathname === '/ai' || pathname.startsWith('/ai/')) return 'ai';
  if (pathname === '/goals' || pathname.startsWith('/goals/')) return 'goals';
  if (pathname === '/stores' || pathname.startsWith('/stores/')) return 'stores';
  if (pathname === '/admin') return 'admin';
  if (pathname === '/comments') return 'comments';
  if (pathname === '/customers' || pathname.startsWith('/customers/')) return 'customer-analysis';
  if (pathname === '/legacy-kpi') return 'legacy-kpi';
  return 'home';
}

const SalonDashboard = () => {
  // Get KPI data from context
  const { kpis, storeAverageKpis, kpiHistory, mlForecasts, aiComment, aiCommentSection1, aiCommentSection2, aiCommentSection3, loading, selectedStylist, selectedStore, selectedMonth } = useDashboard();

  const { user } = useAuth();
  // 管理者 tab = 管理画面 roles only (canAccessIam), not the wider picker set (isAdmin).
  const { role, canAccessIam, canApprove, canApproveGoals, isStylist } = useUserRole();
  // 確認中 count for the 管理者 tab badge — polled so a manager notices new submissions
  // without opening the tab (Phase 1 notification, TASK_AI_ACTION_APPROVAL.md §Notify).
  // Approvers only: company_admin reads the queue but never gets notified.
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    if (!canApprove) {
      setPendingCount(0);
      return undefined;
    }
    let cancelled = false;
    const load = () =>
      loadPendingApprovals()
        .then((d) => { if (!cancelled) setPendingCount(d.count ?? (d.submissions || []).length); })
        .catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [canApprove]);
  // Prefetch the 管理画面 data as soon as a 管理画面 user is signed in and a store/month is known,
  // so the 管理者 tab opens on data already in memory (the backend also caches the roster).
  useEffect(() => {
    if (!canAccessIam || !selectedStore || !selectedMonth) return;
    void prefetchAdminData({ store: selectedStore, month: selectedMonth });
  }, [canAccessIam, selectedStore, selectedMonth]);
  // Push: show messages that arrive while the app is open; refresh this device's token weekly.
  useEffect(() => {
    if (user?.uid) void startPush();
  }, [user?.uid]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fallbackName = user?.email ? user.email.split('@')[0] : 'ゲスト';
  const displayName =
    role === 'dev' && selectedStylist
      ? selectedStylist
      : user?.displayName || fallbackName;

  // Main tabs live in the URL so reload keeps the current page
  const activeMenu = pathToTab(location.pathname);
  const [aiHideTabBar, setAiHideTabBar] = useState(false);
  // Keep AiStack mounted after first visit so Home↔AI does not wipe in-memory chat
  // (conditional unmount was clearing activeSessionId + chatMessages).
  const [aiStackMounted, setAiStackMounted] = useState(() => activeMenu === 'ai');
  /* LEGACY_SHELL: const [sidebarOpen, setSidebarOpen] = useState(false); */

  useEffect(() => {
    if (activeMenu === 'ai') setAiStackMounted(true);
  }, [activeMenu]);

  const handleMenuChange = useCallback((id) => {
    if (id !== 'ai') setAiHideTabBar(false);
    // Keep ?store=&stylist=&month= when switching tabs
    navigate({ pathname: TAB_PATHS[id] || '/', search: location.search });
  }, [navigate, location.search]);
  const [showKpi, setShowKpi] = useState(true);
  const [predictionEnabled, setPredictionEnabled] = useState(false);
  const [targetMonths] = useState(6);
  const [kpiValues, setKpiValues] = useState({
    sales: 500000,
    rate: 70,
    newCustomers: 50,
    unitPriceNew: 6500,
    chartsCount: 90,
    visitCycle: 2.34,
    unitPriceRepeat: 7000,
    retailSales: 300000,
  });
  // Graph KPI selection state
  const [selectedGraph1KPI, setSelectedGraph1KPI] = useState('total_sales');
  const [selectedGraph2KPI, setSelectedGraph2KPI] = useState('nomination_rate');

  const [chartViewportCompact, setChartViewportCompact] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setChartViewportCompact(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const chartHeight = chartViewportCompact ? 300 : 400;
  const chartMarginRight = chartViewportCompact ? 40 : 80;
  const chartTickFontSize = chartViewportCompact ? 11 : 14;

  const updateKpi = (key, value) => {
    setKpiValues((prev) => ({ ...prev, [key]: value }));
    setPredictionEnabled(false);
  };

  // KPI options for dropdown
  const kpiOptions = [
    { value: 'total_sales', label: '総売上', type: 'currency', color: '#FF6E00' },
    { value: 'nomination_rate', label: '指名率', type: 'percent', color: '#33564B' },
    { value: 'new_customer_count', label: '新規客数', type: 'count', color: '#3B82F6' },
    { value: 'new_technical_unit_price', label: '技術単価(新規)', type: 'currency', color: '#8B5CF6' },
    { value: 'repeat_customer_count', label: 'リピート客数', type: 'count', color: '#10B981' },
    { value: 'visit_frequency', label: '来店周期', type: 'decimal', color: '#F59E0B' },
    { value: 'repeat_technical_unit_price', label: '技術単価(リピート)', type: 'currency', color: '#EC4899' },
    { value: 'retail_sales', label: '店販売上', type: 'currency', color: '#06B6D4' },
  ];

  // Format helpers for table display
  const formatTableCurrency = (value) => {
    if (value === null || value === undefined) return '- ';
    return `¥ ${Math.round(value).toLocaleString()}`;
  };
  const formatTablePercent = (value) => {
    if (value === null || value === undefined) return '- ';
    return `${Math.round(value)} %`;
  };
  const formatTableCount = (value, unit) => {
    if (value === null || value === undefined) return '- ';
    return `${Math.round(value)} ${unit}`;
  };
  const formatTableDecimal = (value, unit) => {
    if (value === null || value === undefined) return '- ';
    return `${Number(value).toFixed(2)} ${unit}`;
  };
  
  // Format helpers for graph Y-axis
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '';
    return `¥${(value / 1000).toFixed(0)}K`;
  };
  const formatPercent = (value) => {
    if (value === null || value === undefined) return '';
    return `${Math.round(value)}%`;
  };
  const formatCount = (value) => {
    if (value === null || value === undefined) return '';
    return Math.round(value).toString();
  };
  const formatDecimal = (value) => {
    if (value === null || value === undefined) return '';
    return Number(value).toFixed(2);
  };

  // Create full 12-month timeline and map API data onto it
  const createFullYearData = () => {
    // Map API data first to find last actual month
    const historyMap = {};
    let lastDataMonthKey = '2025-02'; // fallback
    kpiHistory.forEach(item => {
      historyMap[item.month] = {
        total_sales: item.total_sales || 0,
        nomination_rate: item.nomination_rate || 0,
        new_customer_count: item.new_customer_count || 0,
        new_technical_unit_price: item.new_technical_unit_price || null,
        repeat_customer_count: item.repeat_customer_count || 0,
        visit_frequency: item.visit_frequency || 0,
        repeat_technical_unit_price: item.repeat_technical_unit_price || 0,
        retail_sales: item.retail_sales || 0,
      };
      if (item.month > lastDataMonthKey) {
        lastDataMonthKey = item.month;
      }
    });

    // Map ML forecasts by month
    const forecastMap = {};
    mlForecasts.forEach(item => {
      forecastMap[item.month] = {
        total_sales: item.total_sales || null,
        nomination_rate: item.nomination_rate || null,
        new_customer_count: item.new_customer_count || null,
        repeat_customer_count: item.repeat_customer_count || null,
        retail_sales: item.retail_sales || null,
      };
    });
    
    // Parse last data month
    const [lastYear, lastMonth] = lastDataMonthKey.split('-').map(Number);
    const lastDataDate = new Date(lastYear, lastMonth - 1, 1);
    
    // Generate months: from 8 months before last data to targetMonths after
    const months = [];
    const startMonth = new Date(lastDataDate.getFullYear(), lastDataDate.getMonth() - 8, 1);
    const totalMonths = 8 + 1 + targetMonths; // past + last data month + future
    
    for (let i = 0; i < totalMonths; i++) {
      const date = new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = `${date.getMonth() + 1}月`;
      months.push({ monthKey, monthLabel, index: i });
    }
    
    // Find the last data month index (this is where prediction starts)
    const lastDataMonthIndex = months.findIndex(m => m.monthKey === lastDataMonthKey);
    
    // Get last actual values for prediction starting point
    const lastData = historyMap[lastDataMonthKey] || {};
    const lastActualGraph1 = lastData[selectedGraph1KPI] || 0;
    const lastActualGraph2 = lastData[selectedGraph2KPI] || 0;
    
    // Target values from KPI inputs (map to KPI keys)
    const targetMap = {
      total_sales: kpiValues.sales,
      nomination_rate: kpiValues.rate,
      new_customer_count: kpiValues.newCustomers,
      new_technical_unit_price: kpiValues.unitPriceNew,
      repeat_customer_count: kpiValues.chartsCount,
      visit_frequency: kpiValues.visitCycle,
      repeat_technical_unit_price: kpiValues.unitPriceRepeat,
      retail_sales: kpiValues.retailSales,
    };
    const targetGraph1 = targetMap[selectedGraph1KPI] || 0;
    const targetGraph2 = targetMap[selectedGraph2KPI] || 0;
    
    // Extract ML forecast pattern for normalization (only if ML forecast exists)
    const mlForecastPattern1 = [];
    const mlForecastPattern2 = [];
    let mlForecastStart1 = null;
    let mlForecastEnd1 = null;
    let mlForecastStart2 = null;
    let mlForecastEnd2 = null;
    
    // Check if ML forecast exists for selected KPIs
    const hasMLForecast1 = mlForecasts.some(f => f[selectedGraph1KPI] != null);
    const hasMLForecast2 = mlForecasts.some(f => f[selectedGraph2KPI] != null);
    
    if (hasMLForecast1 && predictionEnabled) {
      // Collect ML forecast values for future months (preserve order, keep nulls)
      const futureMonths = months.slice(lastDataMonthIndex + 1, lastDataMonthIndex + 1 + targetMonths);
      const mlValues = futureMonths.map(m => forecastMap[m.monthKey]?.[selectedGraph1KPI] ?? null);
      const mlValuesFiltered = mlValues.filter(v => v != null);
      
      if (mlValuesFiltered.length > 0) {
        mlForecastStart1 = mlValuesFiltered[0];
        mlForecastEnd1 = mlValuesFiltered[mlValuesFiltered.length - 1];
        const range = mlForecastEnd1 - mlForecastStart1;
        // Create normalized pattern (0 to 1 scale), null for missing months
        mlForecastPattern1.push(...mlValues.map(v => {
          if (v == null) return null;
          return range !== 0 ? (v - mlForecastStart1) / range : 0;
        }));
      }
    }
    
    if (hasMLForecast2 && predictionEnabled) {
      const futureMonths = months.slice(lastDataMonthIndex + 1, lastDataMonthIndex + 1 + targetMonths);
      const mlValues = futureMonths.map(m => forecastMap[m.monthKey]?.[selectedGraph2KPI] ?? null);
      const mlValuesFiltered = mlValues.filter(v => v != null);
      
      if (mlValuesFiltered.length > 0) {
        mlForecastStart2 = mlValuesFiltered[0];
        mlForecastEnd2 = mlValuesFiltered[mlValuesFiltered.length - 1];
        const range = mlForecastEnd2 - mlForecastStart2;
        mlForecastPattern2.push(...mlValues.map(v => {
          if (v == null) return null;
          return range !== 0 ? (v - mlForecastStart2) / range : 0;
        }));
      }
    }
    
    return months.map((m, index) => {
      const data = historyMap[m.monthKey];
      const isFuture = m.monthKey > lastDataMonthKey;
      const monthsFromLastData = index - lastDataMonthIndex;
      
      // Calculate prediction values
      let predictedGraph1 = null;
      let predictedGraph2 = null;
      
      if (predictionEnabled && isFuture && monthsFromLastData <= targetMonths) {
        const patternIndex = monthsFromLastData - 1; // -1 because boundary month is index 0
        
        // Graph 1: Use ML pattern if available, else linear
        if (hasMLForecast1 && mlForecastPattern1.length > 0 && patternIndex >= 0 && patternIndex < mlForecastPattern1.length) {
          const normalizedProgress = mlForecastPattern1[patternIndex];
          if (normalizedProgress != null) {
            // Apply ML pattern normalized to lastActual → target
            predictedGraph1 = lastActualGraph1 + (targetGraph1 - lastActualGraph1) * normalizedProgress;
          } else {
            // ML forecast missing for this month, use linear
            const progress = monthsFromLastData / targetMonths;
            predictedGraph1 = lastActualGraph1 + (targetGraph1 - lastActualGraph1) * progress;
          }
        } else {
          // Fallback to linear interpolation
          const progress = monthsFromLastData / targetMonths;
          predictedGraph1 = lastActualGraph1 + (targetGraph1 - lastActualGraph1) * progress;
        }
        
        // Graph 2: Use ML pattern if available, else linear
        if (hasMLForecast2 && mlForecastPattern2.length > 0 && patternIndex >= 0 && patternIndex < mlForecastPattern2.length) {
          const normalizedProgress = mlForecastPattern2[patternIndex];
          if (normalizedProgress != null) {
            predictedGraph2 = lastActualGraph2 + (targetGraph2 - lastActualGraph2) * normalizedProgress;
          } else {
            const progress = monthsFromLastData / targetMonths;
            predictedGraph2 = lastActualGraph2 + (targetGraph2 - lastActualGraph2) * progress;
          }
        } else {
          const progress = monthsFromLastData / targetMonths;
          predictedGraph2 = lastActualGraph2 + (targetGraph2 - lastActualGraph2) * progress;
        }
      }
      
      // For the boundary month (last data), include both actual and prediction start
      const isBoundary = m.monthKey === lastDataMonthKey;
      
      // Get actual values for selected KPIs
      const graph1Actual = data ? data[selectedGraph1KPI] : null;
      const graph2Actual = data ? data[selectedGraph2KPI] : null;

      // Get ML forecast data for this month
      const forecastData = forecastMap[m.monthKey] || null;
      let graph1MLForecast = forecastData ? forecastData[selectedGraph1KPI] : null;
      let graph2MLForecast = forecastData ? forecastData[selectedGraph2KPI] : null;

      // For the last actual data month (boundary), also set forecast to actual value
      // This ensures smooth connection between actual and forecast lines
      if (isBoundary) {
        graph1MLForecast = graph1Actual;
        graph2MLForecast = graph2Actual;
      }
      
      return {
        month: m.monthLabel,
        monthKey: m.monthKey,
        // Actual data (solid line) - dynamic based on selected KPIs
        graph1Actual: graph1Actual,
        graph2Actual: graph2Actual,
        // ML forecast data (dashed line "8 4")
        graph1MLForecast: graph1MLForecast,
        graph2MLForecast: graph2MLForecast,
        // Prediction data (dashed line "5 5") - starts from last actual month
        graph1Predicted: predictionEnabled && (isBoundary ? lastActualGraph1 : predictedGraph1),
        graph2Predicted: predictionEnabled && (isBoundary ? lastActualGraph2 : predictedGraph2),
      };
    });
  };

  const chartData = createFullYearData();
  
  // Get selected KPI configurations
  const graph1Config = kpiOptions.find(opt => opt.value === selectedGraph1KPI) || kpiOptions[0];
  const graph2Config = kpiOptions.find(opt => opt.value === selectedGraph2KPI) || kpiOptions[1];
  
  // Get Y-axis formatter based on KPI type
  const getYAxisFormatter = (type) => {
    switch (type) {
      case 'currency':
        return formatCurrency;
      case 'percent':
        return formatPercent;
      case 'count':
        return formatCount;
      case 'decimal':
        return formatDecimal;
      default:
        return formatCurrency;
    }
  };
  
  // Get Y-axis domain and ticks based on KPI type and data
  const getYAxisConfig = (type, dataKeys) => {
    // Collect all values from all data keys (actual, ML forecast, predicted)
    const allValues = [];
    dataKeys.forEach(key => {
      const values = chartData.map(d => d[key]).filter(v => v != null);
      allValues.push(...values);
    });
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
    
    switch (type) {
      case 'currency':
        const maxCurrency = Math.ceil(maxValue / 100000) * 100000 || 1200000;
        return {
          domain: [0, maxCurrency],
          ticks: Array.from({ length: 7 }, (_, i) => (maxCurrency / 6) * i)
        };
      case 'percent':
        return {
          domain: [0, 100],
          ticks: [0, 25, 50, 75, 100]
        };
      case 'count':
        const maxCount = Math.ceil(maxValue / 10) * 10 || 100;
        return {
          domain: [0, maxCount],
          ticks: Array.from({ length: 6 }, (_, i) => (maxCount / 5) * i)
        };
      case 'decimal':
        const maxDecimal = Math.ceil(maxValue * 2) / 2 || 5;
        return {
          domain: [0, maxDecimal],
          ticks: Array.from({ length: 6 }, (_, i) => (maxDecimal / 5) * i)
        };
      default:
        return {
          domain: [0, 1200000],
          ticks: [0, 200000, 400000, 600000, 800000, 1000000, 1200000]
        };
    }
  };
  
  // Include all data keys that will be displayed on the graph
  const graph1DataKeys = ['graph1Actual', 'graph1MLForecast'];
  if (showKpi && predictionEnabled) {
    graph1DataKeys.push('graph1Predicted');
  }
  const graph2DataKeys = ['graph2Actual', 'graph2MLForecast'];
  if (showKpi && predictionEnabled) {
    graph2DataKeys.push('graph2Predicted');
  }
  
  const graph1YAxis = getYAxisConfig(graph1Config.type, graph1DataKeys);
  const graph2YAxis = getYAxisConfig(graph2Config.type, graph2DataKeys);
  
  // Handler for 変更を反映 button
  const handleApplyKPI = () => {
    setPredictionEnabled(true);
    if (process.env.NODE_ENV === 'development') console.log('🎯 [DEBUG] KPI applied, prediction enabled:', kpiValues);
  };

  /* LEGACY_SHELL — sidebar scroll lock (TopBar/Sidebar hidden in Phase 1)
  useEffect(() => {
    if (!sidebarOpen) {
      document.body.style.overflow = '';
      return undefined;
    }
    const mq = window.matchMedia('(max-width: 767px)');
    const syncBodyScroll = () => {
      document.body.style.overflow = mq.matches ? 'hidden' : '';
    };
    syncBodyScroll();
    mq.addEventListener('change', syncBodyScroll);
    return () => {
      mq.removeEventListener('change', syncBodyScroll);
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeSidebar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen, closeSidebar]);
  */

  if (process.env.NODE_ENV === 'development') {
    console.log('📈 [DEBUG] SalonDashboard - kpiHistory:', kpiHistory);
    console.log('📈 [DEBUG] SalonDashboard - chartData:', chartData);
  }

  const CustomTooltip = ({ active, payload, label, graph1Config, graph2Config }) => {
    if (!active || !payload || payload.length === 0) return null;

    const pickValue = (keys) => {
      const entry = payload.find((p) => keys.includes(p.dataKey) && p.value != null);
      return entry ? entry.value : null;
    };

    const graph1Value = pickValue(['graph1Actual', 'graph1MLForecast', 'graph1Predicted']);
    const graph2Value = pickValue(['graph2Actual', 'graph2MLForecast', 'graph2Predicted']);
    
    // Format value based on KPI type
    const formatValue = (value, type) => {
      if (value == null) return null;
      switch (type) {
        case 'currency':
          return `¥${Number(value).toLocaleString()}`;
        case 'percent':
          return `${Math.round(value)}%`;
        case 'count':
          return `${Math.round(value)}`;
        case 'decimal':
          return Number(value).toFixed(2);
        default:
          return Number(value).toLocaleString();
      }
    };

    return (
      <div className="px-3.5 py-3 bg-white rounded-lg shadow-[0px_3.027px_6.054px_-1.514px_rgba(0,0,0,0.06)] border border-gray-200 flex flex-col items-start gap-2">
        <div className="text-[#333333] text-sm font-semibold font-['Noto Sans'] leading-[21px]">{label} 2025</div>
        <div className="flex flex-col items-start gap-2">
          {graph1Value != null && (
            <div className="flex items-center gap-2">
              <div className="text-[#333333] text-sm font-medium font-['Noto Sans'] leading-[21px]">{graph1Config?.label || 'グラフ1'}</div>
              <div className="text-[#333333] text-base font-medium font-['Noto Sans'] leading-4">
                {formatValue(graph1Value, graph1Config?.type)}
              </div>
            </div>
          )}
          {graph2Value != null && (
            <div className="flex items-center gap-2">
              <div className="text-[#333333] text-sm font-medium font-['Noto Sans'] leading-[21px]">{graph2Config?.label || 'グラフ2'}</div>
              <div className="text-[#333333] text-base font-medium font-['Noto Sans'] leading-4">
                {formatValue(graph2Value, graph2Config?.type)}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (role == null) return; // claims not loaded yet — don't bounce a manager off /admin
    // legacy-kpi: old desktop dashboard (store picker + tables) — never for a stylist
    if (
      (activeMenu === 'admin' && !canAccessIam) ||
      (activeMenu === 'comments' && !isStylist) ||
      // 店舗 tab = 管理画面 roles only; a stylist sees their own record (owner rule 2026-09-29)
      (activeMenu === 'stores' && (!STORES_TAB_ENABLED || !canAccessIam)) ||
      (activeMenu === 'legacy-kpi' && isStylist)
    ) {
      navigate({ pathname: '/', search: location.search }, { replace: true });
    }
  }, [activeMenu, role, canAccessIam, isStylist, navigate, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('legacy') === '1' && location.pathname === '/') {
      navigate({ pathname: '/legacy-kpi', search: location.search }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const showLegacyKpi =
    !isStylist && role != null && (activeMenu === 'legacy-kpi' || (SHOW_LEGACY_KPI && activeMenu === 'home'));

  return (
    <AppShell
      activeTab={activeMenu}
      onTabChange={handleMenuChange}
      displayName={displayName}
      settingsPath={getSettingsPath(searchParams)}
      showAdminTab={canAccessIam}
      showCommentsTab={isStylist}
      showStoresTab={STORES_TAB_ENABLED && canAccessIam}
      pendingCount={pendingCount}
      pageTitle={
        activeMenu === 'goals'
          ? '目標'
          : activeMenu === 'comments'
            ? 'コメント'
            : activeMenu === 'stores'
              ? '店舗'
              : activeMenu === 'admin'
                ? '管理者' // same word as the tab; it showed the person's name (HomeBar fallback)
                : undefined
      }
      hideHomeBar={activeMenu === 'ai'}
      hideTabBar={activeMenu === 'ai' && aiHideTabBar}
    >
      {activeMenu === 'customer-analysis' && <CustomerAnalysisView />}

      {activeMenu === 'home' && <HomeScreen onGoTab={handleMenuChange} />}

      {aiStackMounted && (
        <div
          className={activeMenu === 'ai' ? undefined : 'kuroco-tab-keepalive'}
          hidden={activeMenu !== 'ai'}
          aria-hidden={activeMenu !== 'ai'}
        >
          <AiStack
            displayName={displayName}
            settingsPath={getSettingsPath(searchParams)}
            onSubScreenChange={setAiHideTabBar}
          />
        </div>
      )}

      {activeMenu === 'goals' && <GoalsScreen />}

      {activeMenu === 'stores' && STORES_TAB_ENABLED && <StoresScreen />}

      {activeMenu === 'admin' && canAccessIam && (
        <AdminScreen canApprove={canApprove} canApproveGoals={canApproveGoals} onPendingCount={canApprove ? setPendingCount : undefined} />
      )}

      {activeMenu === 'comments' && isStylist && <CommentsScreen />}

      {/* LEGACY KPI — ?legacy=1 or REACT_APP_SHOW_LEGACY_KPI=true on ホーム */}
      {showLegacyKpi && (
            <>
            {/* KPI Cards */}
            <KPISection />

            {/* Text Content Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4">
              {/* Section 1: Business Activities - AI Generated */}
              <div className="flex flex-col gap-2">
                <div className="flex justify-start items-center gap-2">
                  <div className="w-1 self-stretch bg-[#3F5B3B]" />
                  <div className="text-[#333333] text-xl font-bold font-['Noto Sans'] leading-[30px]">業務・施策実施内容</div>
                </div>
                <div className="p-4">
                  <div className="text-sm text-[#333333] font-medium font-['Noto Sans'] leading-[21px] whitespace-pre-wrap">
                    {loading.aiCommentSection1 ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        AIコメント生成中...
                      </div>
                    ) : !selectedStylist ? (
                      <span className="text-gray-400">スタイリストを選択してください</span>
                    ) : aiCommentSection1 ? (
                      aiCommentSection1
                    ) : (
                      <span className="text-gray-400">AIコメントを取得できませんでした</span>
                    )}
                  </div>
                </div>
              </div>
              {/* Section 2: Results Analysis - AI Generated */}
              <div className="flex flex-col gap-2">
                <div className="flex justify-start items-center gap-2">
                  <div className="w-1 self-stretch bg-[#3F5B3B]" />
                  <div className="text-[#333333] text-xl font-bold font-['Noto Sans'] leading-[30px]">業務・施策結果考察</div>
                </div>
                <div className="p-4 bg-[#FFFBF5] border border-[#CDAF75] rounded">
                  <div className="text-sm text-[#333333] font-medium font-['Noto Sans'] leading-[21px] whitespace-pre-wrap">
                    {loading.aiCommentSection2 ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        AIコメント生成中...
                      </div>
                    ) : !selectedStylist ? (
                      <span className="text-gray-400">スタイリストを選択してください</span>
                    ) : aiCommentSection2 ? (
                      aiCommentSection2
                    ) : (
                      <span className="text-gray-400">AIコメントを取得できませんでした</span>
                    )}
                  </div>
                </div>
              </div>
          </div>

            {/* Performance Section Header with Dropdown */}
            <div className="w-full h-full flex justify-start items-center gap-6 mb-3">
              {/* Left: Performance Title with green bar */}
              <div className="flex justify-start items-center gap-2">
                <div className="w-1 self-stretch bg-[#3F5B3B]" />
                <div className="text-[#333333] text-xl font-bold font-['Noto Sans'] leading-[30px] break-words">実績</div>
              </div>
              
              {/* Right: Target Deadline Selector */}
              <div className="flex justify-start items-center gap-1">
                <div className="text-[#333333] text-sm font-medium font-['Noto Sans'] leading-[21px]">目標期限</div>
                <div className="py-2 px-4 bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] rounded-lg border border-[#E0E0E0] flex justify-start items-center gap-2">
                  <div className="text-[#0E1C18] text-base font-medium font-['Noto Sans'] leading-6">6</div>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" fill="#000000" />
                  </svg>
                </div>
                <div className="text-[#333333] text-sm font-medium font-['Noto Sans'] leading-[21px]">ヶ月</div>
              </div>
            </div>

            {/* Data Table */}
            <div className="mb-4 overflow-x-auto max-w-full">
              <div className="flex min-w-[800px] items-start">
              {/* Left table: 項目名 column only (no card) */}
              <table className="text-xs shrink-0 border-separate border-spacing-0">
                <thead>
                  <tr className="border-t border-b">
                    <th className="sticky left-0 z-20 bg-gray-50 px-2 py-1.5 text-left break-words whitespace-normal border-t border-b shadow-[2px_0_6px_-2px_rgba(0,0,0,0.12)]">項目</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="sticky left-0 z-10 bg-[#E4E7E6] px-2 py-1.5 border-b shadow-[2px_0_6px_-2px_rgba(0,0,0,0.08)]">実績</td>
                  </tr>
                  <tr>
                    <td className="sticky left-0 z-10 bg-[#E4E7E6] px-2 py-1.5 border-b shadow-[2px_0_6px_-2px_rgba(0,0,0,0.08)]">店舗平均</td>
                  </tr>
                  <tr>
                    <td className="sticky left-0 z-10 bg-[#E4E7E6] px-2 py-1.5 text-[#33564B] border-b shadow-[2px_0_6px_-2px_rgba(0,0,0,0.08)]">Target</td>
                  </tr>
                </tbody>
              </table>

              {/* Right table: Data columns with white card background */}
              <Card className="flex-1 min-w-0 rounded-none">
                <table className="min-w-[720px] w-full table-fixed text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-t border-b">
                      <th className="px-2 py-1.5 text-center break-words whitespace-normal">総売上</th>
                      <th className="px-2 py-1.5 text-center break-words whitespace-normal">指名率</th>
                      <th className="px-2 py-1.5 text-center break-words whitespace-normal">新規客数</th>
                      <th className="px-2 py-1.5 text-center break-words whitespace-normal">技術単価(新規)</th>
                      <th className="px-2 py-1.5 text-center break-words whitespace-normal">リピート客数</th>
                      <th className="px-2 py-1.5 text-center break-words whitespace-normal">来店周期</th>
                      <th className="px-2 py-1.5 text-center break-words whitespace-normal">技術単価(リピート)</th>
                      <th className="px-2 py-1.5 text-center break-words whitespace-normal">店販売上</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* 実績 Row - Selected stylist's actual data */}
                    <tr className={loading.kpis ? 'opacity-50' : ''}>
                      <td className="px-2 py-1.5 text-center border-b">{formatTableCurrency(kpis?.total_sales)}</td>
                      <td className="px-2 py-1.5 text-center border-b">{formatTablePercent(kpis?.nomination_rate)}</td>
                      <td className="px-2 py-1.5 text-center border-b">{formatTableCount(kpis?.new_customer_count, '')}</td>
                      <td className="px-2 py-1.5 text-center border-b text-gray-400">- </td>
                      <td className="px-2 py-1.5 text-center border-b">{formatTableCount(kpis?.repeat_customer_count, '')}</td>
                      <td className="px-2 py-1.5 text-center border-b">{formatTableDecimal(kpis?.visit_frequency, '')}</td>
                      <td className="px-2 py-1.5 text-center border-b">{formatTableCurrency(kpis?.repeat_technical_unit_price)}</td>
                      <td className="px-2 py-1.5 text-center border-b">{formatTableCurrency(kpis?.retail_sales)}</td>
                    </tr>
                    {/* 店舗平均 Row - Store-wide averages */}
                    <tr className={loading.storeAverageKpis ? 'opacity-50' : ''}>
                      <td className="px-2 py-1.5 text-center border-b">{formatTableCurrency(storeAverageKpis?.total_sales)}</td>
                      <td className="px-2 py-1.5 text-center border-b">{formatTablePercent(storeAverageKpis?.nomination_rate)}</td>
                      <td className="px-2 py-1.5 text-center border-b">{formatTableCount(storeAverageKpis?.new_customer_count, '')}</td>
                      <td className="px-2 py-1.5 text-center border-b text-gray-400">- </td>
                      <td className="px-2 py-1.5 text-center border-b">{formatTableCount(storeAverageKpis?.repeat_customer_count, '')}</td>
                      <td className="px-2 py-1.5 text-center border-b">{formatTableDecimal(storeAverageKpis?.visit_frequency, '')}</td>
                      <td className="px-2 py-1.5 text-center border-b">{formatTableCurrency(storeAverageKpis?.repeat_technical_unit_price)}</td>
                      <td className="px-2 py-1.5 text-center border-b">{formatTableCurrency(storeAverageKpis?.retail_sales)}</td>
                    </tr>
                    <tr>
                    {/* 総売上 - Plain text, no card */}
                    <td className="px-2 py-1.5 text-center border-b align-middle">
                      <div className="inline-flex min-h-[44px] justify-end items-center gap-1 w-full">
                        <span className="text-[#333333]">¥</span>
                        <input
                          type="number"
                          min={0}
                          step={1000}
                          value={kpiValues.sales}
                          onChange={(e) => updateKpi('sales', Number(e.target.value))}
                          className="min-h-[44px] w-20 bg-transparent outline-none text-right text-[#333333]"
                        />
                      </div>
                    </td>
                    {/* 指名率 - Plain text, no card */}
                    <td className="px-2 py-1.5 text-center border-b align-middle">
                      <div className="inline-flex min-h-[44px] justify-end items-center gap-1 w-full">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={kpiValues.rate}
                          onChange={(e) => updateKpi('rate', Number(e.target.value))}
                          className="min-h-[44px] w-10 bg-transparent outline-none text-right text-[#333333]"
                        />
                        <span className="text-[#333333]">%</span>
                      </div>
                    </td>
                    {/* 新規客数 - Number in white card, unit outside */}
                    <td className="px-2 py-1.5 text-center border-b align-middle">
                      <div className="inline-flex min-h-[44px] justify-end items-center gap-1 w-full">
                        <div className="min-h-[44px] flex items-center px-2 py-1 bg-white rounded shadow-[0px_0.906px_1.812px_0px_rgba(0,0,0,0.05)] border border-[#E4E7E6]">
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={kpiValues.newCustomers}
                            onChange={(e) => updateKpi('newCustomers', Number(e.target.value))}
                            className="min-h-[40px] w-10 bg-transparent outline-none text-center text-[#0E1C18]"
                          />
                        </div>
                        <span className="text-[#333333]"></span>
                      </div>
                    </td>
                    {/* 技術単価(新規客) - ¥ outside, number in white card */}
                    <td className="px-2 py-1.5 text-center border-b align-middle">
                      <div className="inline-flex min-h-[44px] justify-end items-center gap-1 w-full">
                        <span className="text-[#333333]">¥</span>
                        <div className="min-h-[44px] flex items-center px-2 py-1 bg-white rounded shadow-[0px_0.906px_1.812px_0px_rgba(0,0,0,0.05)] border border-[#E4E7E6]">
                          <input
                            type="number"
                            min={0}
                            step={100}
                            value={kpiValues.unitPriceNew}
                            onChange={(e) => updateKpi('unitPriceNew', Number(e.target.value))}
                            className="min-h-[40px] w-16 bg-transparent outline-none text-right text-[#0E1C18]"
                          />
                        </div>
                      </div>
                    </td>
                    {/* カルテ枚数(リピート客) - Number in white card, unit outside */}
                    <td className="px-2 py-1.5 text-center border-b align-middle">
                      <div className="inline-flex min-h-[44px] justify-end items-center gap-1 w-full">
                        <div className="min-h-[44px] flex items-center px-2 py-1 bg-white rounded shadow-[0px_0.906px_1.812px_0px_rgba(0,0,0,0.05)] border border-[#E4E7E6]">
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={kpiValues.chartsCount}
                            onChange={(e) => updateKpi('chartsCount', Number(e.target.value))}
                            className="min-h-[40px] w-10 bg-transparent outline-none text-right text-[#0E1C18]"
                          />
                        </div>
                        <span className="text-[#333333]"></span>
                      </div>
                    </td>
                    {/* 来店周期(リピート客) - Number in white card, unit outside */}
                    <td className="px-2 py-1.5 text-center border-b align-middle">
                      <div className="inline-flex min-h-[44px] justify-end items-center gap-1 w-full">
                        <div className="min-h-[44px] flex items-center px-2 py-1 bg-white rounded shadow-[0px_0.906px_1.812px_0px_rgba(0,0,0,0.05)] border border-[#E4E7E6]">
                          <input
                            type="number"
                            min={0}
                            step={0.01}
                            value={kpiValues.visitCycle}
                            onChange={(e) => updateKpi('visitCycle', Number(e.target.value))}
                            className="min-h-[40px] w-12 bg-transparent outline-none text-right text-[#0E1C18]"
                          />
                        </div>
                        <span className="text-[#333333]">x</span>
                      </div>
                    </td>
                    {/* 技術単価(リピート客) - ¥ outside, number in white card */}
                    <td className="px-2 py-1.5 text-center border-b align-middle">
                      <div className="inline-flex min-h-[44px] justify-end items-center gap-1 w-full">
                        <span className="text-[#333333]">¥</span>
                        <div className="min-h-[44px] flex items-center px-2 py-1 bg-white rounded shadow-[0px_0.906px_1.812px_0px_rgba(0,0,0,0.05)] border border-[#E4E7E6]">
                          <input
                            type="number"
                            min={0}
                            step={100}
                            value={kpiValues.unitPriceRepeat}
                            onChange={(e) => updateKpi('unitPriceRepeat', Number(e.target.value))}
                            className="min-h-[40px] w-16 bg-transparent outline-none text-right text-[#0E1C18]"
                          />
                        </div>
                      </div>
                    </td>
                    {/* 店販売上 - ¥ outside, number in white card */}
                    <td className="px-2 py-1.5 text-center border-b align-middle">
                      <div className="inline-flex min-h-[44px] justify-end items-center gap-1 w-full min-w-0">
                        <span className="text-[#333333] shrink-0">¥</span>
                        <div className="min-h-[44px] flex flex-1 items-center min-w-0 px-2 py-1 bg-white rounded shadow-[0px_0.906px_1.812px_0px_rgba(0,0,0,0.05)] border border-[#E4E7E6]">
                          <input
                            type="number"
                            min={0}
                            step={1000}
                            value={kpiValues.retailSales}
                            onChange={(e) => updateKpi('retailSales', Number(e.target.value))}
                            className="min-h-[40px] w-full bg-transparent outline-none text-right text-[#0E1C18]"
                          />
                        </div>
                      </div>
                    </td>
                    </tr>
                  </tbody>
                </table>
              </Card>
              </div>
            </div>

            {/* 変更を反映 Button */}
            <div className="w-full flex justify-center items-center my-4">
              <div 
                onClick={handleApplyKPI}
                className={`px-14 py-6 rounded-xl inline-flex justify-center items-center gap-0.5 cursor-pointer transition-colors ${
                  predictionEnabled 
                    ? 'bg-[#33564B] hover:bg-[#2a463d]' 
                    : 'bg-[#CBCBCB] hover:bg-[#b8b8b8]'
                }`}
              >
                <div className="text-white text-sm font-bold font-['Noto Sans'] leading-[21px] break-words">
                  {predictionEnabled ? '✓ 反映済み' : '変更を反映'}
                </div>
              </div>
            </div>

            {/* Chart Section */}
            <div className="flex flex-col gap-2">
              <SectionHeader title="実績・予測グラフ" />
              <Card>
                {/* Controls row: グラフ1 + select */}
                <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center mb-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center w-full md:w-auto min-w-0">
                    <div className="text-gray-700 text-base font-medium font-['Noto Sans'] leading-normal shrink-0">グラフ1</div>
                    <div className="relative w-full md:w-48 min-w-0">
                    <select
                      value={selectedGraph1KPI}
                      onChange={(e) => setSelectedGraph1KPI(e.target.value)}
                      className="w-full min-h-[44px] pl-4 pr-8 py-2 bg-white rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border border-neutral-200 text-zinc-800 text-base font-medium font-['Noto Sans'] leading-normal appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#33564B] focus:border-[#33564B]"
                    >
                      {kpiOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                        <path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" fill="#1F2937" />
                      </svg>
                    </div>
                  </div>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center w-full md:w-auto min-w-0">
                    <div className="text-orange-500 text-base font-medium font-['Noto Sans'] leading-normal shrink-0">グラフ2</div>
                    <div className="relative w-full md:w-48 min-w-0">
                      <select
                        value={selectedGraph2KPI}
                        onChange={(e) => setSelectedGraph2KPI(e.target.value)}
                        className="w-full min-h-[44px] pl-4 pr-8 py-2 bg-white rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border border-neutral-200 text-zinc-800 text-base font-medium font-['Noto Sans'] leading-normal appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#33564B] focus:border-[#33564B]"
                      >
                        {kpiOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                          <path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" fill="#1F2937" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <label className="flex items-center gap-2 text-sm py-2 md:py-0 cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="accent-[#33564B] h-5 w-5 shrink-0" 
                      checked={showKpi} 
                      onChange={(e) => setShowKpi(e.target.checked)} 
                    />
                    <span>KPI予測を表示</span>
                  </label>
                </div>
                
                <div className="relative">
                  {loading.kpiHistory ? (
                    <div className="flex items-center justify-center" style={{ height: chartHeight }}>
                      <div className="text-gray-500">読み込み中...</div>
                    </div>
                  ) : (
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <LineChart data={chartData} margin={{ top: 20, right: chartMarginRight, left: 20, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: chartTickFontSize, fontFamily: 'Noto Sans JP', fill: '#001213' }}
                        axisLine={{ stroke: '#E5E7EB' }}
                        tickLine={false}
                      />
                      <YAxis 
                        yAxisId="left"
                        tickFormatter={getYAxisFormatter(graph1Config.type)}
                        domain={graph1YAxis.domain}
                        ticks={graph1YAxis.ticks}
                        tick={{ fontSize: chartTickFontSize, fill: '#001213' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis 
                        yAxisId="right"
                        orientation="right"
                        hide={chartViewportCompact}
                        tickFormatter={getYAxisFormatter(graph2Config.type)}
                        domain={graph2YAxis.domain}
                        ticks={graph2YAxis.ticks}
                        tick={{ fontSize: chartTickFontSize, fill: '#001213' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip content={<CustomTooltip graph1Config={graph1Config} graph2Config={graph2Config} />} />

                      {/* Graph 1 - Actual (solid line) */}
                      <Line 
                        yAxisId="left"
                        type="monotone"
                        dataKey="graph1Actual"
                        stroke={graph1Config.color}
                        strokeWidth={2}
                        dot={{ r: 4, fill: graph1Config.color, stroke: graph1Config.color }}
                        connectNulls={false}
                        name={graph1Config.label}
                      />
                      {/* Graph 1 - ML Forecast (dashed line "8 4") */}
                      <Line 
                        yAxisId="left"
                        type="monotone"
                        dataKey="graph1MLForecast"
                        stroke={graph1Config.color}
                        strokeWidth={2.5}
                        strokeDasharray="8 4"
                        dot={{ r: 3, fill: graph1Config.color, stroke: graph1Config.color }}
                        connectNulls={true}
                        name={`${graph1Config.label} (ML Forecast)`}
                      />
                      {/* Graph 1 - Predicted (dashed line) */}
                      {showKpi && predictionEnabled && (
                        <Line 
                          yAxisId="left"
                          type="monotone"
                          dataKey="graph1Predicted"
                          stroke={graph1Config.color}
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          strokeOpacity={0.6}
                          dot={{ r: 4, fill: graph1Config.color, stroke: graph1Config.color, fillOpacity: 0.6 }}
                          connectNulls={true}
                          name={`${graph1Config.label} (KPI Target)`}
                        />
                      )}

                      {/* Graph 2 - Actual (solid line) */}
                      <Line 
                        yAxisId="right"
                        type="monotone"
                        dataKey="graph2Actual"
                        stroke={graph2Config.color}
                        strokeWidth={2}
                        dot={{ r: 4, fill: graph2Config.color, stroke: graph2Config.color }}
                        connectNulls={false}
                        name={graph2Config.label}
                      />
                      {/* Graph 2 - ML Forecast (dashed line "8 4") */}
                      <Line 
                        yAxisId="right"
                        type="monotone"
                        dataKey="graph2MLForecast"
                        stroke={graph2Config.color}
                        strokeWidth={2.5}
                        strokeDasharray="8 4"
                        dot={{ r: 3, fill: graph2Config.color, stroke: graph2Config.color }}
                        connectNulls={true}
                        name={`${graph2Config.label} (ML Forecast)`}
                      />
                      {/* Graph 2 - Predicted (dashed line) */}
                      {showKpi && predictionEnabled && (
                        <Line 
                          yAxisId="right"
                          type="monotone"
                          dataKey="graph2Predicted"
                          stroke={graph2Config.color}
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          strokeOpacity={0.6}
                          dot={{ r: 4, fill: graph2Config.color, stroke: graph2Config.color, fillOpacity: 0.6 }}
                          connectNulls={true}
                          name={`${graph2Config.label} (KPI Target)`}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                  )}
                </div>

                <div className="mt-6 flex flex-col items-center gap-1">
                  <Button>方向性・月次プランを表示</Button>
                  <p className="text-[10px] text-red-600 font-bold">(※Content cannot be changed once displayed)</p>
                </div>
              </Card>
            </div>

            {/* New Section: 各指標の状況 and 当月実施施策案 */}
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5 mt-4">
              {/* Left Section: KPI Status & Direction - AI Generated */}
              <div className="min-w-0 flex flex-col justify-start items-start gap-2">
                <div className="flex justify-start items-center gap-2">
                  <div className="w-1 self-stretch bg-[#3F5B3B]" />
                  <div className="text-[#333333] text-xl font-bold font-['Noto Sans'] leading-[30px]">各指標の状況および方向性</div>
                </div>
                <div className="self-stretch p-4">
                  <div className="text-[#333333] text-sm font-medium font-['Noto Sans'] leading-[21px] whitespace-pre-wrap">
                    {loading.aiCommentSection3 ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        AIコメント生成中...
                      </div>
                    ) : !selectedStylist ? (
                      <span className="text-gray-400">スタイリストを選択してください</span>
                    ) : aiCommentSection3 ? (
                      aiCommentSection3
                    ) : (
                      <span className="text-gray-400">AIコメントを取得できませんでした</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Card: Monthly Action Plan - WHITE CARD with gold border */}
              <div className="min-w-0 self-stretch flex flex-col justify-start items-start gap-2">
                <div className="flex justify-start items-center gap-2">
                  <div className="w-1 self-stretch bg-[#3F5B3B]" />
                  <div className="text-[#333333] text-xl font-bold font-['Noto Sans'] leading-[30px]">当月実施施策案</div>
                </div>
                <Card className="self-stretch p-4 border border-[#CDAF75] flex flex-col items-end gap-6">
                  <div className="self-stretch">
                    {loading.aiComment ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        AIコメント生成中...
                      </div>
                    ) : !selectedStylist ? (
                      <span className="text-gray-400">スタイリストを選択してください</span>
                    ) : aiComment ? (
                      <div className="text-[#333333] text-sm font-medium font-['Noto Sans'] leading-[21px] whitespace-pre-wrap">
                        {aiComment}
                      </div>
                    ) : (
                      <span className="text-gray-400">AIコメントを取得できませんでした</span>
                    )}
                  </div>
                  {/* AI comments are read-only (owner decision 2026-09-29) — no 編集 / 確認 */}
                </Card>
              </div>
            </div>
            </>
      )}
    </AppShell>
  );
};

export default SalonDashboard;
